import { supabase } from '../lib/supabase';

/**
 * Subscribe to campaign updates for a specific user
 * @param {string} userId - The user ID to subscribe to
 * @param {Function} callback - Callback function to handle updates
 * @returns {Object} Subscription object with unsubscribe method
 */
export const subscribeToCampaigns = (userId, callback) => {
  const channel = supabase
    ?.channel('campaigns-changes')
    ?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'campaigns',
        filter: `artist_id=eq.${userId}`
      },
      (payload) => {
        callback(payload);
      }
    )
    ?.subscribe();

  return {
    unsubscribe: () => {
      supabase?.removeChannel(channel);
    }
  };
};

/**
 * Subscribe to campaign_influencers updates for an influencer
 * @param {string} influencerId - The influencer ID to subscribe to
 * @param {Function} callback - Callback function to handle updates
 * @returns {Object} Subscription object with unsubscribe method
 */
export const subscribeToCampaignInfluencers = (influencerId, callback) => {
  const channel = supabase
    ?.channel('campaign-influencers-changes')
    ?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'campaign_influencers',
        filter: `influencer_id=eq.${influencerId}`
      },
      (payload) => {
        callback(payload);
      }
    )
    ?.subscribe();

  return {
    unsubscribe: () => {
      supabase?.removeChannel(channel);
    }
  };
};

/**
 * Subscribe to notifications for a specific user
 * @param {string} userId - The user ID to subscribe to
 * @param {Function} callback - Callback function to handle updates
 * @returns {Object} Subscription object with unsubscribe method
 */
export const subscribeToNotifications = (userId, callback) => {
  const channel = supabase
    ?.channel('notifications-changes')
    ?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${userId}`
      },
      (payload) => {
        callback(payload);
      }
    )
    ?.subscribe();

  return {
    unsubscribe: () => {
      supabase?.removeChannel(channel);
    }
  };
};

/**
 * Subscribe to influencer profile updates
 * @param {string} userId - The user ID to subscribe to
 * @param {Function} callback - Callback function to handle updates
 * @returns {Object} Subscription object with unsubscribe method
 */
export const subscribeToInfluencerProfile = (userId, callback) => {
  const channel = supabase
    ?.channel('influencer-profile-changes')
    ?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'influencers',
        filter: `user_id=eq.${userId}`
      },
      (payload) => {
        callback(payload);
      }
    )
    ?.subscribe();

  return {
    unsubscribe: () => {
      supabase?.removeChannel(channel);
    }
  };
};

/**
 * Subscribe to multiple tables for comprehensive dashboard updates
 * @param {string} userId - The user ID to subscribe to
 * @param {Object} callbacks - Object containing callback functions for each table
 * @returns {Object} Subscription object with unsubscribe method
 */
export const subscribeToDashboardUpdates = (userId, callbacks = {}) => {
  const subscriptions = [];

  // Subscribe to campaigns if callback provided
  if (callbacks?.onCampaignUpdate) {
    subscriptions?.push(subscribeToCampaigns(userId, callbacks?.onCampaignUpdate));
  }

  // Subscribe to notifications if callback provided
  if (callbacks?.onNotificationUpdate) {
    subscriptions?.push(subscribeToNotifications(userId, callbacks?.onNotificationUpdate));
  }

  // Subscribe to influencer profile if callback provided
  if (callbacks?.onInfluencerUpdate) {
    subscriptions?.push(subscribeToInfluencerProfile(userId, callbacks?.onInfluencerUpdate));
  }

  return {
    unsubscribe: () => {
      subscriptions?.forEach((sub) => sub?.unsubscribe());
    }
  };
};

/**
 * Subscribe to earnings-related updates for influencers
 * @param {string} influencerId - The influencer ID to subscribe to
 * @param {Function} callback - Callback function to handle updates
 * @returns {Object} Subscription object with unsubscribe method
 */
export const subscribeToEarningsUpdates = (influencerId, callback) => {
  // Subscribe to campaign_influencers table for earnings changes
  return subscribeToCampaignInfluencers(influencerId, (payload) => {
    // Filter for earnings-related changes (allocated_budget, proposal_accepted)
    if (payload?.new?.allocated_budget !== payload?.old?.allocated_budget ||
        payload?.new?.proposal_accepted !== payload?.old?.proposal_accepted) {
      callback(payload);
    }
  });
};